// Canonical forecast-progress step ladders — CLIENT-OWNED, fixed lists the backend's
// stage/step signals map INTO. The running chat line shows "Step N/X — <label>"
// (see formatProgressStep); X is the ladder length, not the backend's variable turn
// count, so it's always a clean "N of X".
//
// ENGINE-AWARE: the two served tiers emit different progress vocabularies, so each
// gets its own ladder (picked by tag via ladderForTag):
//   • DEEP (the agentic engine) emits distinct phase steps (plan_built/agent_turn/
//     critiquing/composing_report + panel signals) — a genuine 7-phase arc, mapped
//     from the step/stage names directly.
//   • QUICK has a 4-step ladder (Queued → Researching → Weighing → Composing).
//     Researching and Composing come from the server's stage (researching /
//     postprocess). Weighing is PACED rather than server-signalled unless a lane sends
//     a climbing `consolidating` counter (bucketed by QUICK_LADDER.stepIndex): after
//     QUICK_WEIGHING_DWELL_MS on Researching the client advances the line to Weighing
//     (pacedQuickStepIndex; the timer lives in hooks/useQuickLadderPacer.js). Only a
//     real postprocess frame moves it to Composing.
//
// Robustness contract: the backend may add/rename/drop stages or steps. Anything a
// ladder doesn't recognize maps to -1 and the caller/reducer KEEPS the current step
// (never crashes, resets, or regresses) — a backend change can only make us less
// granular, never break the display.

import { streamsResearch } from "./deepProgress.js";

// Normalize a raw progress value (0–1 fraction OR 0–100 int) to a 0–100 number, or
// 0 when absent — used to bucket the quick engine's climbing counter into phases.
function pctOf(progress) {
  const p = Number(progress);
  if (!Number.isFinite(p)) return 0;
  return p > 0 && p < 1 ? p * 100 : p;
}

// ── DEEP ladder (the full agentic arc; mapped from step/stage names) ─────────
const DEEP_STEPS = [
  { key: "preprocess", label: "Checking your question" },
  { key: "queued", label: "Queued" },
  { key: "planning", label: "Planning research" },
  { key: "researching", label: "Researching sources" },
  { key: "weighing", label: "Weighing evidence" },
  { key: "consolidating", label: "Finding triggers" },
  { key: "composing", label: "Writing the forecast" },
];
const DEEP_STEP_TO_IDX = {
  admitted: 1,
  plan_built: 2,
  agent_turn: 3,
  panel_model_done: 3,
  searching: 3,
  reading_sources: 3,
  search_round: 3,
  research_step: 3,
  critiquing: 4,
  filtering_evidence: 4,
  summarizing_round: 4,
  consolidating: 5,
  synthesizing: 5,
  composing_report: 6,
};
const DEEP_STAGE_TO_IDX = { preprocess: 0, queued: 1, researching: 3, postprocess: 6 };

const DEEP_LADDER = {
  steps: DEEP_STEPS,
  stepIndex(stage, step, pct) {
    let named = -1;
    if (step && Object.prototype.hasOwnProperty.call(DEEP_STEP_TO_IDX, step)) {
      named = DEEP_STEP_TO_IDX[step];
    } else if (stage && Object.prototype.hasOwnProperty.call(DEEP_STAGE_TO_IDX, stage)) {
      // Stage-only progress need not use the agent_turn counter's scale.
      return DEEP_STAGE_TO_IDX[stage];
    }
    // The agentic "researching" loop dominates a deep run — the whole agent_turn loop
    // maps to Researching (step 4/7) by name, so the step would sit there statically.
    // Advance it through the mid-ladder as the loop's counter climbs (agent_turn
    // progress spans ~0–85) so the step number keeps moving. max() keeps it monotonic
    // and lets a later NAMED signal (critiquing/composing_report) still jump ahead;
    // Only from the Researching step onward (gate on the NAMED index, not the stage —
    // plan_built also carries stage "researching" but must stay Planning at step 3/7).
    if (named >= 3) {
      const p = pctOf(pct);
      const byProgress = p >= 55 ? 5 : p >= 30 ? 4 : 3;
      return Math.max(named, byProgress);
    }
    return named;
  },
};

// ── QUICK ladder (signalled Researching/Composing; Weighing is paced — see header) ──
const QUICK_STEPS = [
  { key: "queued", label: "Queued" },
  { key: "researching", label: "Researching sources" },
  { key: "weighing", label: "Weighing evidence" },
  { key: "composing", label: "Writing the forecast" },
];

const QUICK_LADDER = {
  steps: QUICK_STEPS,
  // `queued`/preprocess sit at step 0; any researching frame is Researching and the
  // postprocess frame is Composing. A climbing `consolidating` counter, when a lane
  // sends one, is bucketed into Weighing/Composing; current quick lanes don't, so
  // Weighing is reached by pacing instead (pacedQuickStepIndex).
  stepIndex(stage, step, pct) {
    if (step === "admitted" || stage === "queued" || stage === "preprocess") return 0;
    // The working loop: `consolidating` matched by STEP (so a stage-less frame still
    // resolves) or any researching/postprocess signal → a phase chosen by how far the
    // counter has climbed.
    if (
      step === "consolidating" ||
      step === "synthesizing" ||
      stage === "researching" ||
      stage === "postprocess"
    ) {
      const p = pctOf(pct);
      if (stage === "postprocess" || p >= 73) return 3; // upper third / post → Composing
      if (p >= 56) return 2; // middle third → Weighing evidence
      return 1; // 40..55 (band floor) and anything below → Researching
    }
    return -1;
  },
};

// Deep is the default (unknown/untagged engines get the full ladder).
export const LADDERS = { deep: DEEP_LADDER, quick: QUICK_LADDER };

/** How long a quick run sits on Researching before the line is paced to Weighing. */
export const QUICK_WEIGHING_DWELL_MS = 25_000;
const QUICK_RESEARCHING_IDX = QUICK_STEPS.findIndex((s) => s.key === "researching");
const QUICK_WEIGHING_IDX = QUICK_STEPS.findIndex((s) => s.key === "weighing");

/**
 * Whether a run's step line is eligible for pacing: a quick-tier run currently on
 * Researching. Deep (and untagged) runs never pace.
 * @param {string|null} tag  forecaster tier tag
 * @param {number|null|undefined} stepIdx  the run's current ladder step
 * @returns {boolean}
 */
export function isPaceableQuickStep(tag, stepIdx) {
  return tag === "quick" && stepIdx === QUICK_RESEARCHING_IDX;
}

/**
 * The paced step for a quick run, or -1 when pacing doesn't apply: Weighing once the
 * run has sat on Researching for `dwellMs`. Never returns Composing (only the real
 * postprocess frame moves there) and never a step behind the current one.
 * @param {string|null} tag  forecaster tier tag
 * @param {number|null|undefined} stepIdx  the run's current ladder step
 * @param {number} elapsedMs  time since the run entered Researching
 * @param {number} [dwellMs]
 * @returns {number}
 */
export function pacedQuickStepIndex(tag, stepIdx, elapsedMs, dwellMs = QUICK_WEIGHING_DWELL_MS) {
  if (!isPaceableQuickStep(tag, stepIdx)) return -1;
  return elapsedMs >= dwellMs ? QUICK_WEIGHING_IDX : -1;
}

/**
 * Pick the step ladder for a forecaster tier tag ("quick" / "deep" / null).
 * Anything that isn't explicitly "quick" gets the DEEP ladder (safe default).
 * @param {string|null} tag
 * @returns {{steps: {key:string,label:string}[], stepIndex: Function}}
 */
export function ladderForTag(tag) {
  return tag === "quick" ? QUICK_LADDER : DEEP_LADDER;
}

/**
 * Map a backend (stage, step[, pct]) to an index within `ladder`, or -1 when
 * unrecognized. `pct` (0–100 or a 0–1 fraction) is only consulted by the quick
 * ladder, which buckets its climbing counter into synthetic phases; the deep
 * ladder ignores it.
 * @param {object} ladder  a LADDERS entry (from ladderForTag)
 * @param {string|null} stage
 * @param {string|null} step
 * @param {number|null} [pct]
 * @returns {number} 0..ladder.steps.length-1, or -1 for unknown
 */
export function progressStepIndex(ladder, stage, step, pct = null) {
  return ladder.stepIndex(stage, step, pct);
}

/**
 * Build the "Step N/X — <label>" line for an index within `ladder`. The Researching
 * step appends a live round/model counter when the backend passes it (agent_turn's
 * {turn, max_turns} or panel_model_done's {done, total}); other steps are the bare
 * label. Returns null for an out-of-range index.
 * @param {object} ladder
 * @param {number} idx
 * @param {object|null} [data]  the progress frame's `data` blob (optional)
 * @returns {string|null}
 */
export function formatProgressStep(ladder, idx, data) {
  if (!Number.isInteger(idx) || idx < 0 || idx >= ladder.steps.length) return null;
  const count = ladder.steps.length;
  let label = `Step ${idx + 1}/${count} — ${ladder.steps[idx].label}`;
  if (ladder.steps[idx].key === "researching" && data && typeof data === "object") {
    const turn = Number(data.turn);
    const maxTurns = Number(data.max_turns);
    const done = Number(data.done);
    const total = Number(data.total);
    if (Number.isFinite(turn) && Number.isFinite(maxTurns) && maxTurns > 0) {
      label += ` (round ${turn} of ${maxTurns})`;
    } else if (Number.isFinite(done) && Number.isFinite(total) && total > 0) {
      label += ` (pass ${done} of ${total})`;
    }
  }
  return label;
}

/**
 * The "opening" ladder steps BEFORE the Researching working phase — the ones the
 * backend typically blows through in well under a second (Preprocessing, Queued,
 * Planning on deep) so they never render. Returned in order as ready-to-dispatch
 * {stepIdx, message} entries, for a paced synthetic intro at forecast start (see
 * startProgressIntro). Empty when the ladder has no pre-Researching steps.
 * @param {string|null} tag  forecaster tier tag
 * @returns {{stepIdx: number, message: string}[]}
 */
export function introStepsForTag(tag) {
  const ladder = ladderForTag(tag);
  const researchIdx = ladder.steps.findIndex((s) => s.key === "researching");
  // Steps strictly BEFORE the Researching phase. Empty when there's no "researching"
  // step (Math.max(0,-1)=0) — never pace the whole ladder for an unexpected shape.
  const upto = Math.max(0, researchIdx); // exclusive
  const out = [];
  for (let i = 0; i < upto; i++) {
    out.push({ stepIdx: i, message: formatProgressStep(ladder, i, null) });
  }
  return out;
}

// ── Run status card (components/RunStatus.jsx) ─────────────────────────────
// One short, tier-independent list of what Sayer is doing. Every ladder step folds into one
// of these four, so a backend that adds or renames steps only makes the card less granular.
export const RUN_STATUS_STAGES = Object.freeze([
  "Checking your question",
  "Researching sources",
  "Finding triggers",
  "Writing the forecast",
]);
const LADDER_KEY_TO_RUN_STAGE = {
  preprocess: 0,
  queued: 1,
  planning: 1,
  researching: 1,
  weighing: 2,
  consolidating: 2,
  composing: 3,
};

/**
 * Which RUN_STATUS_STAGES entry a run is on, from its status and live progress.
 * @param {string|null} tag  forecaster tier tag ("quick" / "deep" / null)
 * @param {{stage?: string|null, step?: string|number|null, stepIdx?: number, pct?: number|null}|null} progress
 * @param {string} status  run status
 * @returns {number} 0..3
 */
export function runStatusStageIndex(tag, progress, status) {
  if (status === "preprocessing" || !progress) return 0;
  const ladder = ladderForTag(tag);
  let idx = Number.isInteger(progress.stepIdx) ? progress.stepIdx : -1;
  if (idx < 0)
    idx = progressStepIndex(ladder, progress.stage ?? null, progress.step ?? null, progress.pct);
  const key = idx >= 0 ? ladder.steps[idx]?.key : null;
  if (key && Object.prototype.hasOwnProperty.call(LADDER_KEY_TO_RUN_STAGE, key))
    return LADDER_KEY_TO_RUN_STAGE[key];
  if (progress.stage === "postprocess") return 3;
  if (progress.stage === "researching" || progress.stage === "queued") return 1;
  return 0;
}

// The answer a reforecast keeps on its contract when the run has no server probability: the
// contract's own (older rows) or a multiway distribution's mass.
export function contractHasAnswer(contract) {
  if (typeof contract?.probability === "number" && Number.isFinite(contract.probability)) {
    return true;
  }
  const mass = contract?.forecast_value?.representation?.mass;
  return Array.isArray(mass) && mass.length > 0;
}

/**
 * Whether the canvas shows the run status card: a first run with no answer to show yet. An
 * update of a forecast that already has one keeps showing that forecast, and a run streaming
 * a live partial forecast shows that instead. With live progress on (the
 * chatboard-forecast-live-progress flag, without which no research stream arrives), a run that
 * streams its research (a Mirabilis lane) keeps its card until it finishes
 * (lib/forecast/deepProgress.ts).
 * @param {object|null} run  a run-store Run
 * @param {{liveProgress?: boolean}} [opts]
 * @returns {boolean}
 */
export function showsRunStatus(run, { liveProgress = false } = {}) {
  if (!run) return false;
  if (run.status !== "preprocessing" && run.status !== "running") return false;
  if ((run.events?.length ?? 0) > 0) return false;
  // An update of a forecast that has an answer keeps showing it, triggers or not.
  if (run.serverPrediction != null) return false;
  if (liveProgress && streamsResearch(run)) return !contractHasAnswer(run.contract);
  if ((run.progressForecastRevisions ?? 0) > 0) return false;
  return true;
}
